import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Slide,
  Typography,
} from '@mui/material';
import React from 'react';
import { AdminConversation } from '../../services/admin/FidesChatAdminService';
import { formatConversationPreviewText } from '../../utils/chatParticipantAddedNote';
import { Palette } from 'Palette';
import {
  FIDES_CHAT_TRAY_EDGE_OFFSET,
  FIDES_CHAT_TRAY_HEIGHT,
  FIDES_CHAT_TRAY_RIGHT_OFFSET,
  FIDES_CHAT_TRAY_WIDTH,
  formatConversationTime,
  getDisplayName,
  getInitials,
} from './fidesChatTrayUtils';

interface FidesChatInboxPanelProps {
  open: boolean;
  loading: boolean;
  conversations: AdminConversation[];
  currentAdminExternalId: string | null;
  onClose: () => void;
  onOpenConversation: (conversationId: string) => void;
}

export function FidesChatInboxPanel({
  open,
  loading,
  conversations,
  currentAdminExternalId,
  onClose,
  onOpenConversation,
}: FidesChatInboxPanelProps) {
  return (
    <Slide direction="up" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: FIDES_CHAT_TRAY_EDGE_OFFSET + 64 + 12,
          right: FIDES_CHAT_TRAY_RIGHT_OFFSET,
          width: FIDES_CHAT_TRAY_WIDTH,
          height: FIDES_CHAT_TRAY_HEIGHT,
          maxHeight: `calc(100vh - ${FIDES_CHAT_TRAY_EDGE_OFFSET + 64 + 28}px)`,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
          zIndex: 1300,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            backgroundColor: Palette.default.buttonBg,
            color: '#fff',
          }}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
            Investor chat inbox
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close chat tray"
            sx={{ color: '#fff', p: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', backgroundColor: '#fff' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : conversations.length === 0 ? (
            <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No active conversations
              </Typography>
            </Box>
          ) : (
            conversations.map((conversation, index) => {
              const displayName = getDisplayName(conversation);
              return (
                <React.Fragment key={conversation.conversationId}>
                  <Box
                    onClick={() => onOpenConversation(conversation.conversationId)}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      cursor: 'pointer',
                      alignItems: 'center',
                      backgroundColor: conversation.unreadByAdmin
                        ? 'rgba(20, 60, 52, 0.06)'
                        : '#fff',
                      '&:hover': {
                        backgroundColor: 'rgba(20, 60, 52, 0.08)',
                      },
                    }}
                  >
                    <Avatar sx={{ bgcolor: Palette.default.buttonBg, width: 40, height: 40 }}>
                      {getInitials(displayName)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
                          {displayName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatConversationTime(conversation.lastMessageAt)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.8rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatConversationPreviewText(conversation, currentAdminExternalId)}
                      </Typography>
                    </Box>
                    {conversation.unreadByAdmin && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: '#d32f2f',
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                  {index < conversations.length - 1 && <Divider />}
                </React.Fragment>
              );
            })
          )}
        </Box>
      </Paper>
    </Slide>
  );
}
